import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, Trash2 } from 'lucide-react';
import { Button } from '@/app/components/ui/button';
import { Input } from '@/app/components/ui/input';
import { Textarea } from '@/app/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/app/components/ui/card';
import { Label } from '@/app/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/app/components/ui/select';
import { LocationSearchComboBox } from '@/components/shared/location-search-combo-box';
import type { LocationOption } from '@/components/shared/location-search-combo-box';
import { JobSearchComboBox } from '@/components/shared/job-search-combo-box';
import type { JobOption } from '@/components/shared/job-search-combo-box';
import { useCreateIncidentReport } from '@/hooks/use-safety';
import { useOrgToday } from '@/hooks/use-settings';
import { PersonSearchComboBox, type PersonOption } from '@/components/shared/person-search-combo-box';
import { OfflineIndicator } from '@/app/components/offline/offline-indicator';
import type { IncidentSeverity, IncidentCategory } from '@/lib/types';

interface WitnessRow {
  tech_id: number | null;
  person_id: number | null;
  name: string;
}

const severityOptions: { value: IncidentSeverity; label: string; color: string }[] = [
  { value: 'minor', label: 'Minor', color: 'border-green-500 bg-green-50 text-green-700' },
  { value: 'moderate', label: 'Moderate', color: 'border-yellow-500 bg-yellow-50 text-yellow-700' },
  { value: 'serious', label: 'Serious', color: 'border-orange-500 bg-orange-50 text-orange-700' },
  { value: 'critical', label: 'Critical', color: 'border-red-500 bg-red-50 text-red-700' },
];

const categoryOptions: { value: IncidentCategory; label: string }[] = [
  { value: 'fall', label: 'Fall' },
  { value: 'struck_by', label: 'Struck By' },
  { value: 'caught_between', label: 'Caught Between' },
  { value: 'electrical', label: 'Electrical' },
  { value: 'other', label: 'Other' },
];

export function CreateIncidentPage() {
  const navigate = useNavigate();
  const createMutation = useCreateIncidentReport();
  const orgToday = useOrgToday();

  const [title, setTitle] = useState('');
  const [date, setDate] = useState(orgToday);
  const [time, setTime] = useState('');
  const [selectedLocation, setSelectedLocation] = useState<LocationOption | null>(null);
  const [location, setLocation] = useState('');
  const [selectedProject, setSelectedProject] = useState<JobOption | null>(null);
  const [description, setDescription] = useState('');
  const [severity, setSeverity] = useState<IncidentSeverity>('moderate');
  const [category, setCategory] = useState<IncidentCategory>('other');
  const [injuredPartyPerson, setInjuredPartyPerson] = useState<PersonOption | null>(null);
  const [injuredParty, setInjuredParty] = useState('');
  const [witnessRows, setWitnessRows] = useState<WitnessRow[]>([]);
  const [witnesses] = useState('');
  const [immediateActions, setImmediateActions] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const incident = await createMutation.mutateAsync({
        title,
        date,
        time: time || undefined,
        location_fk: selectedLocation?.id || null,
        location,
        description,
        severity,
        category,
        project: selectedProject?.id,
        injured_party_person: injuredPartyPerson?.person_id ?? undefined,
        injured_party: injuredParty || injuredPartyPerson?.name || undefined,
        witness_records: witnessRows
          .filter((w) => w.name.trim())
          .map((w) => ({
            person: w.person_id ?? undefined,
            name: w.name,
          })),
        witnesses: witnesses || undefined,
        immediate_actions: immediateActions || undefined,
      });
      navigate(`/safety/incidents/${incident.id}`);
    } catch {
      // error handled by mutation
    }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6 max-w-4xl mx-auto">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/safety/incidents')}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Report Incident</h1>
          <p className="text-gray-600">Document a safety incident for investigation</p>
        </div>
      </div>

      <OfflineIndicator />

      <form onSubmit={handleSubmit} className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle>Incident Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="title">Title *</Label>
              <Input
                id="title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Brief description of the incident"
                required
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <Label htmlFor="date">Date *</Label>
                <Input
                  id="date"
                  type="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  required
                />
              </div>
              <div>
                <Label htmlFor="time">Time</Label>
                <Input
                  id="time"
                  type="time"
                  value={time}
                  onChange={(e) => setTime(e.target.value)}
                />
              </div>
              <div>
                <Label>Project</Label>
                <JobSearchComboBox
                  value={selectedProject}
                  onChange={(job) => {
                    setSelectedProject(job);
                    if (job?.location_fk_display && !selectedLocation) {
                      setSelectedLocation({
                        id: job.location_fk_display.id,
                        name: job.location_fk_display.name,
                        type: job.location_fk_display.type,
                      });
                    }
                  }}
                />
              </div>
              <div>
                <Label>Location</Label>
                <LocationSearchComboBox value={selectedLocation} onChange={setSelectedLocation} />
              </div>
            </div>
            <div>
              <Label htmlFor="location">Specific Area / Details</Label>
              <Input
                id="location"
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                placeholder="e.g. East face, 3rd floor scaffolding"
              />
            </div>
            <div>
              <Label htmlFor="category">Category *</Label>
              <Select
                value={category}
                onValueChange={(value) => setCategory(value as IncidentCategory)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  {categoryOptions.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="description">Description *</Label>
              <Textarea
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={4}
                placeholder="Describe what happened in detail..."
                required
              />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Severity</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {severityOptions.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  className={`border-2 rounded-lg p-4 text-center font-medium transition-all ${
                    severity === option.value
                      ? option.color
                      : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
                  }`}
                  onClick={() => setSeverity(option.value)}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>People Involved</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>Injured Party</Label>
              <PersonSearchComboBox
                endpoint="persons"
                value={injuredPartyPerson}
                onChange={(person) => {
                  setInjuredPartyPerson(person);
                  setInjuredParty(person ? person.name : '');
                }}
                placeholder="Search for injured person..."
              />
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label>Witnesses</Label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setWitnessRows([...witnessRows, { tech_id: null, person_id: null, name: '' }])}
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Add Witness
                </Button>
              </div>
              {witnessRows.length === 0 ? (
                <p className="text-gray-500 text-sm">
                  No witnesses added. Click &quot;Add Witness&quot; to add one.
                </p>
              ) : (
                <div className="space-y-2">
                  {witnessRows.map((row, index) => (
                    <div key={index} className="flex items-center gap-2">
                      <div className="flex-1">
                        <PersonSearchComboBox
                          endpoint="persons"
                          value={row.name ? { tech_id: row.tech_id, person_id: row.person_id, name: row.name } : null}
                          onChange={(person) => {
                            const updated = [...witnessRows];
                            updated[index] = {
                              tech_id: person?.tech_id ?? null,
                              person_id: person?.person_id ?? null,
                              name: person?.name ?? '',
                            };
                            setWitnessRows(updated);
                          }}
                        />
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => setWitnessRows(witnessRows.filter((_, i) => i !== index))}
                      >
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Immediate Response</CardTitle>
          </CardHeader>
          <CardContent>
            <div>
              <Label htmlFor="actions">Immediate Actions Taken</Label>
              <Textarea
                id="actions"
                value={immediateActions}
                onChange={(e) => setImmediateActions(e.target.value)}
                rows={3}
                placeholder="What was done immediately after the incident..."
              />
            </div>
          </CardContent>
        </Card>

        <div className="flex justify-end gap-3">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate('/safety/incidents')}
          >
            Cancel
          </Button>
          <Button type="submit" disabled={createMutation.isPending}>
            {createMutation.isPending ? 'Submitting...' : 'Submit Report'}
          </Button>
        </div>
      </form>
    </div>
  );
}
